import React, { useEffect, useState } from "react";
import { CheckCircle2, Flame, MapPin, Loader2, Camera } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export function CheckinBar({ onPosted }) {
  const [activity, setActivity] = useState("");
  const [location, setLocation] = useState("");
  const [photo, setPhoto] = useState("");
  const [showExtra, setShowExtra] = useState(false);
  const [busy, setBusy] = useState(false);
  const [streak, setStreak] = useState(0);
  const [todayDone, setTodayDone] = useState(false);

  const loadStreak = async () => {
    try { const { data } = await api.get("/checkin/streak"); setStreak(data.streak || 0); setTodayDone(!!data.checked_in_today); }
    catch { /* silent */ }
  };
  useEffect(() => { loadStreak(); }, []);

  const checkin = async () => {
    if (!activity.trim()) { toast.error("What are you up to? 🙂"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkin", { activity: activity.trim(), location: location.trim(), media_url: photo.trim() });
      setStreak(data.streak); setTodayDone(true);
      setActivity(""); setLocation(""); setPhoto(""); setShowExtra(false);
      toast.success(`Checked in! 🔥 ${data.streak}-day streak`);
      onPosted?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rud-card rounded-2xl p-4 mt-6" data-testid="checkin-bar">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-medium"><CheckCircle2 size={16} className="text-brand" /> Check in — share what you're up to</div>
        <div className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${streak > 0 ? "bg-orange-500/15 text-orange-400" : "bg-white/5 text-sec"}`} data-testid="checkin-streak">
          <Flame size={13} className={streak > 0 ? "fill-current" : ""} /> {streak}-day streak{todayDone ? " · done today ✅" : ""}
        </div>
      </div>
      <div className="flex gap-2">
        <input data-testid="checkin-activity" value={activity} onChange={(e) => setActivity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && checkin()}
          placeholder="e.g. Meditating by the Ganga 🧘 / Learning Python / Volunteering" className="rud-input flex-1 rounded-full px-4 py-2.5 text-sm" />
        <button data-testid="checkin-submit" onClick={checkin} disabled={busy} className="btn-primary rounded-full px-5 text-sm flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Check in
        </button>
      </div>
      <button data-testid="checkin-extra-toggle" onClick={() => setShowExtra((s) => !s)} className="text-[11px] text-sec hover:text-brand mt-2 flex items-center gap-1"><MapPin size={11} /> Add place / photo</button>
      {showExtra && (
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          <div className="relative">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="checkin-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (optional)" className="rud-input w-full rounded-lg pl-9 pr-3 py-2 text-sm" />
          </div>
          <div className="relative">
            <Camera size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="checkin-photo" value={photo} onChange={(e) => setPhoto(e.target.value)} placeholder="Photo URL (optional)" className="rud-input w-full rounded-lg pl-9 pr-3 py-2 text-sm" />
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckinBar;
